/**
 * MailDeliverabilityCard + MailDeliverabilityBanner (OCP-621).
 *
 * Bounces and complaints this period against the SES bars, next to the
 * quota bar on /mail. Before this, a complaint was visible only as an amber
 * pill on the Sends page: a customer who never filtered by status never saw
 * it, and learned about a spam complaint from a raw AWS abuse report in
 * their support inbox (10monkeys, 2026-09). The banner appears at warning
 * / critical and names the bar; the card is always there.
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export type DeliverabilityLevel = 'ok' | 'warning' | 'critical' | 'unknown';

export interface Deliverability {
  reported: boolean;
  bounces: number | null;
  complaints: number | null;
  bounce_rate_pct: number | null;
  complaint_rate_pct: number | null;
  level: DeliverabilityLevel;
  binding: 'complaints' | 'bounces' | null;
  bars: {
    complaint_warning_pct: number;
    complaint_critical_pct: number;
    bounce_warning_pct: number;
    bounce_critical_pct: number;
  };
}

export function levelClasses(level: DeliverabilityLevel): string {
  if (level === 'ok') return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-200 dark:border-emerald-900/50';
  if (level === 'warning') return 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-900/50';
  if (level === 'critical') return 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900/50';
  return 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
}

function pct(n: number | null): string {
  return n === null ? '-' : `${n.toFixed(1)}%`;
}

export function MailDeliverabilityCard({ deliverability }: { deliverability: Deliverability | null }) {
  const { t } = useTranslation('mail-quota');
  const d = deliverability;
  return (
    <section
      data-testid="mail-deliverability-card"
      className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
    >
      <header className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t('deliverability.title')}</h2>
        <span
          data-testid="mail-deliverability-level"
          className={`inline-flex rounded border px-1.5 py-0.5 text-xs ${levelClasses(d?.level ?? 'unknown')}`}
        >
          {t(`deliverability.level.${d?.level ?? 'unknown'}`)}
        </span>
      </header>
      {!d || !d.reported ? (
        <p className="mt-3 text-sm text-gray-500">
          {t('deliverability.notReported')}
        </p>
      ) : (
        <dl className="mt-3 grid grid-cols-2 gap-4 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">{t('deliverability.complaints')}</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {d.complaints ?? 0}
              <span className="ml-2 text-sm font-normal text-gray-500">{pct(d.complaint_rate_pct)}</span>
            </dd>
            <dd className="text-xs text-gray-500">{t('deliverability.complaintBars', { warning: d.bars.complaint_warning_pct, critical: d.bars.complaint_critical_pct })}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-gray-500">{t('deliverability.bounces')}</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">
              {d.bounces ?? 0}
              <span className="ml-2 text-sm font-normal text-gray-500">{pct(d.bounce_rate_pct)}</span>
            </dd>
            <dd className="text-xs text-gray-500">{t('deliverability.bounceBars', { warning: d.bars.bounce_warning_pct, critical: d.bars.bounce_critical_pct })}</dd>
          </div>
        </dl>
      )}
      <p className="mt-3 text-xs text-gray-500">
        {t('deliverability.explainBefore')}{' '}
        <Link to="/mail/suppression?reason=complaint" className="text-brand-500 hover:underline">{t('deliverability.explainLink')}</Link>.
      </p>
    </section>
  );
}

export function MailDeliverabilityBanner({ deliverability }: { deliverability: Deliverability | null }) {
  const { t } = useTranslation('mail-quota');
  const d = deliverability;
  if (!d || !d.reported || (d.level !== 'warning' && d.level !== 'critical')) return null;
  const onComplaints = d.binding === 'complaints';
  const text = onComplaints
    ? t(d.level === 'critical' ? 'deliverability.banner.complaintsCritical' : 'deliverability.banner.complaintsWarning', {
        count: d.complaints,
        rate: pct(d.complaint_rate_pct),
        warning: d.bars.complaint_warning_pct,
        critical: d.bars.complaint_critical_pct,
      })
    : t(d.level === 'critical' ? 'deliverability.banner.bouncesCritical' : 'deliverability.banner.bouncesWarning', {
        count: d.bounces,
        rate: pct(d.bounce_rate_pct),
        warning: d.bars.bounce_warning_pct,
        critical: d.bars.bounce_critical_pct,
      });
  return (
    <div
      role="alert"
      data-testid="mail-deliverability-banner"
      className={`mb-4 rounded border px-3 py-2 text-sm ${levelClasses(d.level)}`}
    >
      {text}{' '}
      <Link to={onComplaints ? '/mail/sends?status=complained' : '/mail/sends?status=bounced'} className="font-medium underline">
        {onComplaints ? t('deliverability.banner.viewComplained') : t('deliverability.banner.viewBounced')}
      </Link>
    </div>
  );
}
